import { getSiteName } from '@provision/formatting-utils';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { AddPreSoldMembershipFormValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import {
  convertPriceToCents,
  getIntervalFromLengthInMonths,
} from 'src/components/AddPreSoldMembershipModal/util';
import { AddSiteStep } from 'src/components/AddSiteModal/add-site-step';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer, useModalSteps } from 'src/components/ModalContainer';
import { formatProjectDate } from 'src/components/SidePanelTabs/Projects/use-project-card-form';
import {
  Address,
  Project,
  ProjectStage,
  ProjectStages,
  useCreateProject,
  useGroupRules,
  useProjectStages,
  UserGroupRules,
} from 'src/graphql';
import { usePreSoldMembership } from 'src/graphql/entities/pre-sold-membership/pre-sold-membership';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import {
  AddProjectFormField,
  AddProjectFormValues,
  useAddProjectForm,
} from './use-add-project-form';

interface AddProjectFieldsProps {
  formMethods: ReturnType<typeof useForm<AddProjectFormValues>>;
  projectStages: ProjectStage[];
  loading: boolean;
  partnerId?: number | null;
}

const AddProjectFields = ({
  formMethods,
  projectStages,
  loading,
  partnerId,
}: AddProjectFieldsProps) => {
  const { pushStep } = useModalSteps();

  const addNewSite = async () => {
    const site = await pushStep<Address>(<AddSiteStep partnerId={partnerId} />);
    if (!site) {
      return;
    }
    formMethods.setValue(
      AddProjectFormField.Address,
      {
        id: site.addressId,
        label: getSiteName(site),
        partnerId: site.partnerId,
        hasPreSoldMembership: false,
      },
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const { addProjectFormFields } = useAddProjectForm({
    projectStages,
    formMethods,
    addNewSite: () => void addNewSite(),
  });

  return (
    <GenericForm<AddProjectFormValues> formMethods={formMethods} loading={loading}>
      {addProjectFormFields}
    </GenericForm>
  );
};

export const AddProjectModal = () => {
  const { closeModal } = useModalMethods(Modals.AddProject);
  const { isOpened, modalProps } = useModalState(Modals.AddProject);
  const { showModal: openPsmModal, closeModal: closePsmModal } = useModalMethods(
    Modals.AddPreSoldMembership,
  );
  const { groupRules } = useGroupRules();

  const canUpdateSLA = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);
  const { createProject } = useCreateProject({
    sort: modalProps?.sort,
    filters: modalProps?.filters,
    limit: modalProps?.limit,
    offset: modalProps?.offset,
  });
  const { showSuccessAlert } = useNotifications();

  const { onSuccessSubmit } = modalProps || {};

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { createPreSoldMembership } = usePreSoldMembership();

  const createOnSubmitPsm = useCallback(
    (addressId: string, partnerId: number) => async (data: AddPreSoldMembershipFormValues) => {
      const { intervalCount, intervalUnit } = getIntervalFromLengthInMonths(
        data.lengthInMonths,
      );
      const psmPayload = {
        addressId,
        partnerId,
        amount: convertPriceToCents(data.price),
        intervalUnit,
        intervalCount,
        partnerProductId: data.partnerProductId,
        isScheduled: false,
      };

      closePsmModal();
      const result = await createPreSoldMembership(psmPayload);
      if (!result?.errors) {
        showSuccessAlert('Pre-sold membership created successfully');
      }
    },
    [closePsmModal, createPreSoldMembership, showSuccessAlert],
  );

  const { projectStages: allProjectStages, loading: projectStagesLoading } =
    useProjectStages();

  const projectStages = useMemo(() => {
    return allProjectStages
      .filter((stage) => stage.id !== ProjectStages.ProposalLost)
      .sort((a, b) => a.id - b.id);
  }, [allProjectStages]);

  const address = modalProps?.address;

  const defaultValues = useMemo<AddProjectFormValues>(
    () => ({
      [AddProjectFormField.Address]: address
        ? {
            id: address.addressId || '',
            label: address.fullAddress || '',
            partnerId: address.partnerId,
            hasPreSoldMembership: !!address.preSoldMemberships?.length,
          }
        : null,
      [AddProjectFormField.Name]: '',
      [AddProjectFormField.ProjectStage]:
        projectStages.find((stage) => stage.id === ProjectStages.ActiveProject) ?? null,
      [AddProjectFormField.ProjectManager]: null,
      [AddProjectFormField.SigningDate]: null,
      [AddProjectFormField.EstimatedDay1]: null,
      [AddProjectFormField.WarrantyExpirationDate]: null,
      [AddProjectFormField.Day1Date]: null,
      [AddProjectFormField.TosAccepted]: address?.tosAccepted === 'Yes',
      [AddProjectFormField.WillReceiveEmails]: false,
      [AddProjectFormField.HasPreSoldMembership]: false,
    }),
    [address, projectStages],
  );

  const formMethods = useForm<AddProjectFormValues>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched',
  });
  const { handleSubmit, reset } = formMethods;

  const close = useCallback(() => {
    reset();
    closeModal();
  }, [closeModal, reset]);

  const onSubmit = handleSubmit(async (data) => {
    const addressPartnerId = data[AddProjectFormField.Address]?.partnerId;
    const partnerId = selectedPartnerId ?? addressPartnerId;
    const stageId = data[AddProjectFormField.ProjectStage]?.id;

    if (!data[AddProjectFormField.Address]?.id || !partnerId || isNullish(stageId)) {
      return;
    }

    close();

    const payload = {
      partnerId,
      addressId: data[AddProjectFormField.Address].id,
      name: data[AddProjectFormField.Name],
      projectStageId: stageId,
      warrantyExpiration:
        stageId === ProjectStages.ServiceClient &&
        !!data[AddProjectFormField.WarrantyExpirationDate]
          ? formatProjectDate(data[AddProjectFormField.WarrantyExpirationDate])
          : null,
      projectManagerId: data[AddProjectFormField.ProjectManager]?.id,
      signingDate:
        stageId === ProjectStages.Proposal && !!data[AddProjectFormField.SigningDate]
          ? formatProjectDate(data[AddProjectFormField.SigningDate])
          : null,
      estimatedDay1:
        stageId === ProjectStages.ActiveProject && !!data[AddProjectFormField.EstimatedDay1]
          ? formatProjectDate(data[AddProjectFormField.EstimatedDay1])
          : null,
      day1Date:
        stageId === ProjectStages.ServiceClient && !!data[AddProjectFormField.Day1Date]
          ? formatProjectDate(data[AddProjectFormField.Day1Date])
          : null,
      tosAccepted: data[AddProjectFormField.TosAccepted] ? 'Yes' : 'No',
      willReceiveEmails: data[AddProjectFormField.WillReceiveEmails],
    };

    const result = await createProject(payload, address);
    if (result.errors) {
      return;
    }

    if (data[AddProjectFormField.HasPreSoldMembership] && canUpdateSLA) {
      openPsmModal({
        addressId: data[AddProjectFormField.Address].id,
        onSubmit: createOnSubmitPsm(data[AddProjectFormField.Address].id, partnerId),
      });
    }
    onSuccessSubmit?.(result.data?.createProjectWithSideEffects.project as Project);
    showSuccessAlert('Project added successfully');
  });

  return (
    <ModalContainer
      open={isOpened}
      title="Create project"
      onClose={close}
      data-testid="add-project-modal"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-project-cancel' },
        {
          label: 'Create project',
          kind: 'primary',
          onClick: onSubmit,
          disabled: projectStagesLoading,
          testId: 'add-project-submit',
        },
      ]}
    >
      <AddProjectFields
        formMethods={formMethods}
        projectStages={projectStages}
        loading={projectStagesLoading}
        partnerId={address?.partnerId ?? selectedPartnerId}
      />
    </ModalContainer>
  );
};
